import { useMemo, useState } from 'react';
import { Box, Button, CardContent, Collapse, IconButton, MenuItem, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';

const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value) || 0);

export default function RevenueLine({ category = [], lineData = [] }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [collapsed, setCollapsed] = useState(false);
    const [startYear, setStartYear] = useState('');
    const [endYear, setEndYear] = useState('');
    const annual = useMemo(() => category.map((year, index) => ({ year: String(year), amount: Number(lineData[index]) || 0 }))
        .sort((a, b) => Number(a.year) - Number(b.year)), [category, lineData]);
    const first = startYear || annual[0]?.year || '';
    const last = endYear || annual[annual.length - 1]?.year || '';
    const selected = annual.filter(row => Number(row.year) >= Number(first) && Number(row.year) <= Number(last));
    const option = {
        backgroundColor: '#08111d',
        legend: { top: 4, right: 12, textStyle: { color: 'rgba(241, 245, 249, 0.82)', fontSize: isMobile ? 10 : 11 }, itemWidth: 18, itemHeight: 10, data: ['Revenue'] },
        tooltip: {
            trigger: 'axis', axisPointer: { type: 'cross', label: { backgroundColor: '#2d3138', color: '#f8fafc' } },
            backgroundColor: 'rgba(7, 13, 24, 0.96)', borderColor: 'rgba(148, 163, 184, 0.2)', textStyle: { color: '#f8fafc' },
            valueFormatter: money,
        },
        grid: { left: isMobile ? 44 : 58, right: isMobile ? 16 : 24, top: 56, bottom: isMobile ? 84 : 68, containLabel: true },
        xAxis: {
            type: 'category', boundaryGap: false, data: selected.map(row => row.year),
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.08)' } }, axisTick: { show: false },
            axisLabel: { rotate: isMobile ? 35 : 0, fontSize: isMobile ? 10 : 12, color: 'rgba(226, 232, 240, 0.72)' },
            splitLine: { show: true, lineStyle: { color: 'rgba(255, 255, 255, 0.06)' } },
        },
        yAxis: {
            type: 'value', axisLine: { show: false }, axisTick: { show: false },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.06)' } },
            axisLabel: { formatter: money, fontSize: isMobile ? 10 : 12, color: 'rgba(226, 232, 240, 0.72)' },
        },
        dataZoom: [
            { type: 'inside', start: 0, end: 100 },
            { type: 'slider', start: 0, end: 100, height: isMobile ? 18 : 22, bottom: isMobile ? 12 : 18,
                fillerColor: 'rgba(255, 255, 255, 0.08)', borderColor: 'rgba(255, 255, 255, 0.05)', backgroundColor: 'rgba(8, 17, 29, 0.82)',
                dataBackground: { lineStyle: { color: 'rgba(255, 255, 255, 0.22)' }, areaStyle: { color: 'rgba(255, 255, 255, 0.05)' } },
                textStyle: { color: 'rgba(226, 232, 240, 0.7)' }, handleStyle: { color: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.28)' } },
        ],
        series: [{ name: 'Revenue', type: 'line', smooth: true, showSymbol: true, symbol: 'circle', symbolSize: isMobile ? 5 : 7,
            lineStyle: { width: 3, color: '#69f0ae' }, itemStyle: { color: '#69f0ae' }, data: selected.map(row => row.amount) }],
    };

    return <div className="col-xl-12 col-md-12" style={{ marginBottom: '1em' }}>
        <div className="card">
            <div className="card-header">
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
                        <IconButton onClick={() => setCollapsed(previous => !previous)} size="small" sx={{ mr: 1, mt: 0.25 }} aria-label="Toggle annual revenue chart">
                            {collapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Year-over-Year Revenue</Typography>
                            <Typography variant="body2" color="text.secondary">Recorded revenue by ship year across the selected range.</Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Revenue In Range</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{money(selected.reduce((sum, row) => sum + row.amount, 0))}</Typography>
                    </Box>
                </Stack>
            </div>
            <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
                        <TextField select label="Start Year" size="small" value={first} disabled={!annual.length} onChange={event => setStartYear(event.target.value)}
                            sx={{ minWidth: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            {annual.filter(row => Number(row.year) <= Number(last)).map(row => <MenuItem key={row.year} value={row.year}>{row.year}</MenuItem>)}
                        </TextField>
                        <TextField select label="End Year" size="small" value={last} disabled={!annual.length} onChange={event => setEndYear(event.target.value)}
                            sx={{ minWidth: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            {annual.filter(row => Number(row.year) >= Number(first)).map(row => <MenuItem key={row.year} value={row.year}>{row.year}</MenuItem>)}
                        </TextField>
                        <Button variant="outlined" onClick={() => { setStartYear(''); setEndYear(''); }} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>Reset to All Years</Button>
                    </Stack>
                    <Box sx={{ background: 'linear-gradient(180deg, #0b1524 0%, #07101b 100%)', borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.06)', overflow: 'hidden', p: { xs: 1, sm: 1.5 }, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                        {selected.length ? <ReactECharts theme="v5" option={option} style={{ height: isMobile ? '360px' : '440px', width: '100%' }} opts={{ renderer: 'svg' }} />
                            : <Typography sx={{ p: 5, color: '#e2e8f0', textAlign: 'center' }}>No recorded revenue is available for this range yet.</Typography>}
                    </Box>
                </CardContent>
            </Collapse>
        </div>
    </div>;
}
